import { useAuth } from '@/contexts/AuthContext'
import { getUserRoleFlags } from '@/components/layout/navigationSections'
import { useAccessibleAffiliates } from './useAccessibleAffiliates'
import { hasAffiliateLeft } from './useAffiliateLeftPlatform'

export interface DedicatedOnlyGate {
  /**
   * True when the user's ONLY affiliate access is Dedicated Affiliates —
   * or, #1324, affiliates that have left the platform.
   */
  hideDiscovery: boolean
  /** False while the flag is still being fetched for gated users. */
  isResolved: boolean
}

/**
 * #1210 — "Dedicated Affiliate" Discovery gate.
 *
 * A dedicated affiliate keeps every standard affiliate capability except
 * Discovery: the sidebar item is hidden and /discovery redirects to the
 * dashboard. The classification lives on the affiliate entity
 * (partner_service.affiliates.is_dedicated), so the gate applies only when
 * ALL of the user's accessible affiliates carry it — a user with mixed
 * access (one dedicated + one standard affiliate) legitimately keeps
 * Discovery, as do hotel-access users and admins.
 *
 * Only affiliate-only non-admins ever fire the /affiliates/mine request
 * (usually already cached by their landing page); everyone else resolves
 * synchronously with no fetch. An empty affiliate list resolves to
 * hideDiscovery=false — absence of data must never hide navigation.
 *
 * Staleness is bounded by the shared 5-minute my-accessible-affiliates
 * cache: an admin flipping the flag mid-session takes effect on the user's
 * next refetch. That is deliberate — the server-side discovery exclusion
 * and forced discovery_visible=false are the real enforcement; this hook is
 * UX only. Do not "fix" it by shortening the staleTime.
 */
export function useDedicatedOnlyAffiliate(): DedicatedOnlyGate {
  const { user } = useAuth()
  const { isAdmin, hasHotelAccess, hasAffiliateAccess } = getUserRoleFlags(user)
  const needsCheck = !!user && !isAdmin && !hasHotelAccess && hasAffiliateAccess

  const { affiliates, isLoading } = useAccessibleAffiliates({ enabled: needsCheck })

  if (!needsCheck) {
    return { hideDiscovery: false, isResolved: true }
  }
  // #1324 — an affiliate that left the platform loses Discovery entirely, so
  // the gate closes when every accessible affiliate is dedicated OR left.
  return {
    hideDiscovery:
      affiliates.length > 0 &&
      affiliates.every(a => a.isDedicated === true || hasAffiliateLeft(a)),
    isResolved: !isLoading,
  }
}
